import React from "react";
import { useAiUsage } from "./useAiUsage";
import { featureUsage, formatDate, formatDecimal, isZero } from "./helpers";
import "./AiUsage.css";

/**
 * Shows a company's AI credit balance and what its AI features have consumed.
 *
 * @param {object} props - Component props.
 * @param {string} [props.highlightFeature] - AiBillableFeature to summarise above the table.
 * @param {boolean} [props.showBreakdown] - Whether to list every feature.
 * @param {string} [props.title] - Heading text.
 * @returns {JSX.Element} The usage panel.
 *
 * @remarks
 * Features that do not record what they cost are listed with an explicit "not
 * yet tracked" tag and excluded from the totals. Showing them as zero would
 * read as free usage, which is the one wrong answer a billing panel can give.
 */
const AiUsagePanel = ({
  highlightFeature = null,
  showBreakdown = true,
  title = "AI usage",
}) => {
  const { summary, loading, error, reload } = useAiUsage();

  if (loading) {
    return (
      <div className="aiu-panel">
        <p className="aiu-title">{title}</p>
        <p className="aiu-muted">Loading…</p>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="aiu-panel aiu-error">
        <p className="aiu-title">{title}</p>
        <p className="aiu-muted">{error || "No usage available."}</p>
        <button type="button" className="aiu-retry" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  const highlighted = highlightFeature
    ? featureUsage(summary, highlightFeature)
    : null;
  const low = isZero(summary.balance.credits);

  return (
    <div className="aiu-panel">
      <div className="aiu-top">
        <div>
          <p className="aiu-title">{title}</p>
          <div className={`aiu-balance ${low ? "aiu-balance-low" : ""}`}>
            <span className="aiu-balance-value">
              {formatDecimal(summary.balance.credits, 2)}
            </span>
            <span className="aiu-balance-unit">credits remaining</span>
          </div>
        </div>
      </div>

      <div className="aiu-stats">
        {highlighted && highlighted.metered && (
          <>
            <div className="aiu-stat">
              <span className="aiu-stat-label">Analyses run</span>
              <span className="aiu-stat-value">{highlighted.runs}</span>
            </div>
            <div className="aiu-stat">
              <span className="aiu-stat-label">Credits used here</span>
              <span className="aiu-stat-value">
                {formatDecimal(highlighted.credits, 2)}
              </span>
            </div>
            <div className="aiu-stat">
              <span className="aiu-stat-label">Last used</span>
              <span className="aiu-stat-value">
                {formatDate(highlighted.lastUsedAt)}
              </span>
            </div>
          </>
        )}

        {(!highlighted || !highlighted.metered) && (
          <>
            <div className="aiu-stat">
              <span className="aiu-stat-label">Credits used, all features</span>
              <span className="aiu-stat-value">
                {formatDecimal(summary.totalCredits, 2)}
              </span>
            </div>
            <div className="aiu-stat">
              <span className="aiu-stat-label">Rate</span>
              <span className="aiu-stat-value">
                {formatDecimal(summary.balance.creditsPerUsd, 0)} / $1
              </span>
            </div>
          </>
        )}
      </div>

      {showBreakdown && (
        <div className="aiu-table-wrap">
          <table className="aiu-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Runs</th>
                <th>Credits used</th>
                <th>Last used</th>
              </tr>
            </thead>
            <tbody>
              {summary.features.map((row) => (
                <tr
                  key={row.feature}
                  className={row.metered ? "" : "aiu-untracked"}
                >
                  <td>
                    {row.label}
                    {!row.metered && (
                      <span className="aiu-tag">not yet tracked</span>
                    )}
                  </td>
                  <td className="aiu-num">{row.metered ? row.runs : "—"}</td>
                  <td className="aiu-num">
                    {row.metered ? formatDecimal(row.credits, 2) : "—"}
                  </td>
                  <td className="aiu-num">
                    {row.metered ? formatDate(row.lastUsedAt) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="aiu-note">
        Credits are charged on what a feature actually costs to run, plus a
        {` ${formatDecimal(summary.balance.markupPercent, 0)}%`} service margin.
        Failed runs are not charged, and repeating an unchanged analysis is free.
      </p>
    </div>
  );
};

export default AiUsagePanel;
